import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import MenuIcon from "../ui/icons/MenuIcon";
import { useAuthStore } from "../../store/authStore";
import useLogout from "../../hooks/useLogout";
import hfcLogo from "../../assets/hfc_logo_from_original_badge_v1.png";

export const Header = () => {
  const { user } = useAuthStore();
  const { handleLogout } = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const navTo = (path) => {
    navigate(path);
    closeMenu();
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const NavLink = ({ path, label }) => (
    <button
      onClick={() => navTo(path)}
      className={`font-heading text-sm font-bold uppercase tracking-wider transition-colors duration-150 px-3 py-2 rounded
        ${isActive(path)
          ? "text-flyers-orange border-b-2 border-flyers-orange"
          : "text-flyers-white hover:text-flyers-orange"
        }`}
    >
      {label}
    </button>
  );

  const MobileNavLink = ({ path, label, onClick }) => (
    <button
      onClick={onClick || (() => navTo(path))}
      className={`font-heading text-2xl font-bold uppercase tracking-wider py-3 transition-colors duration-150
        ${path && isActive(path) ? "text-flyers-orange" : "text-flyers-white hover:text-flyers-orange"}`}
    >
      {label}
    </button>
  );

  return (
    <header className="sticky top-0 z-50 bg-flyers-black border-b border-flyers-dark-gray">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-28 sm:h-32">
          {/* Brand */}
          <button
            onClick={() => navTo("/")}
            className="flex items-center gap-2 sm:gap-3 font-heading text-xl sm:text-2xl md:text-3xl font-black uppercase tracking-wide text-flyers-white hover:text-flyers-orange transition-colors duration-150"
          >
            <span className="h-[104px] w-[104px] sm:h-[116px] sm:w-[116px] rounded-full overflow-hidden flex-shrink-0">
              <img
                src={hfcLogo}
                alt="Hockey Fight Chronicles logo"
                className="h-full w-full object-cover"
              />
            </span>
            <span className="whitespace-nowrap">
              Hockey Fight <span className="text-flyers-orange">Chronicles</span>
            </span>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            <NavLink path="/games" label="Games" />
            <NavLink path="/fights" label="Fights" />
            <NavLink path="/leagues" label="Leagues" />
            <NavLink path="/players" label="Players" />
            <NavLink path="/teams" label="Teams" />
            {user?.role === "admin" && (
              <>
                <NavLink path="/admin" label="Admin" />
                <NavLink path="/admin/dashboard" label="Dashboard" />
                <NavLink path="/admin/create" label="Create Fight" />
              </>
            )}
            {user ? (
              <button
                onClick={handleLogout}
                className="font-heading text-sm font-bold uppercase tracking-wider text-flyers-mid-gray hover:text-flyers-orange transition-colors duration-150 px-3 py-2 ml-2 border border-flyers-dark-gray rounded hover:border-flyers-orange"
              >
                Logout
              </button>
            ) : (
              <button
                onClick={() => navTo("/login")}
                className="font-heading text-sm font-bold uppercase tracking-wider bg-flyers-orange text-flyers-white px-4 py-2 rounded hover:bg-flyers-orange-light transition-colors duration-150 ml-2"
              >
                Login
              </button>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-flyers-orange hover:text-flyers-orange-light transition-colors"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6.34 4.93l12.73 12.73-1.41 1.41L4.93 6.34z" />
                <path d="M4.93 17.66L17.66 4.93l1.41 1.41L6.34 19.07z" />
              </svg>
            ) : (
              <MenuIcon width="28" height="28" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Overlay Menu */}
      {isMenuOpen && (
        <div className="md:hidden fixed inset-0 top-28 sm:top-32 z-40 bg-flyers-black/95 backdrop-blur-sm">
          <nav className="flex flex-col items-center justify-center gap-2 pt-12 px-6">
            <MobileNavLink path="/games" label="Games" />
            <MobileNavLink path="/fights" label="Fights" />
            <MobileNavLink path="/leagues" label="Leagues" />
            <MobileNavLink path="/players" label="Players" />
            <MobileNavLink path="/teams" label="Teams" />
            {user?.role === "admin" && (
              <>
                <div className="w-16 border-t border-flyers-dark-gray my-2" />
                <MobileNavLink path="/admin" label="Admin" />
                <MobileNavLink path="/admin/dashboard" label="Dashboard" />
                <MobileNavLink path="/admin/create" label="Create Fight" />
              </>
            )}
            <div className="w-16 border-t border-flyers-dark-gray my-2" />
            {user ? (
              <MobileNavLink
                label="Logout"
                onClick={() => { handleLogout(); closeMenu(); }}
              />
            ) : (
              <MobileNavLink path="/login" label="Login" />
            )}
          </nav>
        </div>
      )}
    </header>
  );
};
